// src/components/LegalPages.jsx
// Páginas públicas de Termos de Uso e Política de Privacidade.
// Texto revisado pelo Dr. Bruno em 23/09/2026 e atualizado conforme sua orientação de 13/10/2026. Qualquer alteração no
// conteúdo jurídico deve passar por revisão do advogado novamente —
// não editar o texto abaixo sem validação jurídica.
import React from 'react';

function LegalPageShell({ titulo, atualizadoEm, children }) {
  return (
    <div className="min-h-screen bg-black text-white/90 px-6 py-12">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="text-xs uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors">
          ← Voltar
        </a>
        <h1 className="text-2xl md:text-3xl font-black text-white mt-6 mb-2">{titulo}</h1>
        <p className="text-xs text-white/40 mb-10">Última atualização: {atualizadoEm}</p>
        <div className="space-y-6 text-sm leading-relaxed text-white/80 [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-base [&_h2]:mt-8 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse [&_td]:border [&_td]:border-white/10 [&_td]:p-2 [&_th]:border [&_th]:border-white/10 [&_th]:p-2 [&_th]:text-left">
          {children}
        </div>
      </div>
    </div>
  );
}

export function TermosDeUso() {
  return (
    <LegalPageShell titulo="Termos de Uso — Phoenix OS" atualizadoEm="13/10/2026">
      <p>
        O Phoenix OS é um software (SaaS) de diagnóstico financeiro, operado por Vantum Tecnologia
        (Samuel Benevides do Nascimento LTDA), CNPJ 69.171.174/0001-13. Ao usar o Phoenix OS, você
        concorda com estes Termos.
      </p>

      <h2>1. O que o Phoenix OS é (e o que não é)</h2>
      <p>
        O Phoenix OS gera diagnósticos, projeções e pareceres com base nos dados financeiros que
        você insere, usando inteligência artificial.
      </p>
      <p>
        Isso não é consultoria financeira, contábil ou de investimentos profissional. As análises
        são ferramentas de apoio à decisão, baseadas nos dados que você fornece — a precisão do
        resultado depende da precisão dos dados inseridos. Decisões financeiras importantes devem
        sempre considerar orientação de um profissional habilitado (contador, consultor financeiro),
        quando aplicável.
      </p>

      <h2>2. Cadastro e conta</h2>
      <p>
        Você é responsável por manter a confidencialidade da sua senha e por todas as atividades
        realizadas na sua conta. Avise-nos imediatamente (suporte@phoenixos.com.br) se suspeitar de
        acesso não autorizado.
      </p>

      <h2>3. Planos e pagamento</h2>
      <ul>
        <li>Os preços e funcionalidades de cada plano estão descritos no app/site no momento da contratação.</li>
        <li>Assinaturas são cobradas de forma recorrente (mensal), processadas via Asaas, até que sejam canceladas.</li>
        <li>Pagamentos avulsos (ex: diagnóstico único) não são recorrentes.</li>
        <li>Alterações de preço serão comunicadas com antecedência mínima de 30 dias e valem para os ciclos seguintes à comunicação.</li>
        <li>Se o pagamento de uma assinatura não for identificado, mantemos o acesso por 48 horas e, depois disso, o acesso ao plano é suspenso até a regularização.</li>
      </ul>

      <h2>4. Cancelamento</h2>
      <p>
        Você pode cancelar sua assinatura a qualquer momento pelo app ou pelo painel. O cancelamento
        interrompe cobranças futuras e você mantém o acesso até o fim do período já pago, sem
        reembolso proporcional desse período.
      </p>
      <p>
        Depois que o acesso ao plano termina, os relatórios gerados durante o plano ficam bloqueados
        (não são apagados) e voltam a abrir se você assinar novamente. Os relatórios comprados de
        forma avulsa continuam acessíveis.
      </p>
      <p>
        Direito de arrependimento: você pode desistir da contratação em até 7 dias corridos, tanto de
        assinaturas quanto do relatório avulso (Phoenix Entry), com devolução do valor pago, conforme
        a <a href="/reembolso" className="underline text-white">Política de Reembolso</a>. Ao devolver o valor, o acesso ao plano ou ao relatório é encerrado.
      </p>

      <h2>5. Dados inseridos por Consultores sobre terceiros</h2>
      <p>
        Se você usa o perfil Consultor para analisar empresas de clientes seus, você declara ter
        autorização desses clientes para inserir os dados financeiros deles no Phoenix OS, e é
        responsável por essa autorização perante seus próprios clientes. 
      </p>
      <p>
        Você se responsabiliza pela exatidão dos dados inseridos em nossa plataforma, inclusive quanto
        à regularidade do procedimento de coleta destes. A Phoenix OS responde apenas pelo tratamento
        dos dados fornecidos por você.
      </p>
      <p>
        Se você assina também o perfil Gestor, aplicam-se as condições comerciais informadas no
        momento da contratação, inclusive eventual desconto temporário na assinatura ativa.
      </p>

      <h2>6. Uso aceitável</h2>
      <p>Você concorda em não: (a) tentar acessar dados de outras contas sem autorização; (b) fazer engenharia reversa do software; (c) usar o serviço para fins ilegais.</p>

      <h2>7. Propriedade intelectual</h2>
      <p>
        O software, marca "Phoenix OS" e todo o conteúdo do produto pertencem à Vantum Tecnologia.
        Os dados que você insere continuam sendo seus.
      </p>

      <h2>8. Limitação de responsabilidade</h2>
      <p>
        A Vantum Tecnologia não é agente financeiro, instituição financeira, administrador de
        fundos, gestor de fundos ou consultor de investimentos, cabendo tão somente ao usuário a
        responsabilidade pela tomada de decisões financeiras, ainda que com base em análises da
        Phoenix OS.
      </p>
      <p>
        A plataforma Phoenix OS oferece serviço de meio, e não de resultado, sendo certo que a
        precisão das análises também depende da exatidão dos dados inseridos, daí porque o usuário
        deve estar ciente de que a análise da PhoenixOS, por si só, não a coloca como responsável ou
        corresponsável por eventuais danos que o usuário venha suportar.
      </p>
      <p>
        A plataforma Phoenix OS também não se responsabiliza por indisponibilidades temporárias do
        serviço em razão de culpa exclusiva do usuário, de terceiros ou por motivo de força maior.
      </p>

      <h2>9. Alterações destes Termos</h2>
      <p>Podemos atualizar estes Termos periodicamente, com aviso prévio razoável para mudanças relevantes.</p>

      <h2>10. Foro e legislação aplicável</h2>
      <p>Estes Termos são regidos pelas leis brasileiras. Fica eleito o foro da comarca de Curitiba - PR para dirimir eventuais controvérsias.</p>

      <h2>11. Aceite da Política de Privacidade</h2>
      <p>
        Para criar uma conta ou contratar qualquer plano ou relatório avulso, você precisa marcar a caixa de
        aceite, declarando que leu e que concorda com estes Termos e com a nossa{' '}
        <a href="/politica-de-privacidade" className="underline text-white">política de privacidade</a>.
        Guardamos o registro desse aceite (data, hora e versão dos textos).
      </p>

      <h2>12. Consentimento com a coleta de dados pessoais</h2>
      <p>
        Ao marcar a caixa de aceite, você consente expressamente com a coleta e o tratamento dos
        seguintes dados pessoais: nome, e-mail, CPF ou CNPJ, informados no momento da criação de
        conta ou da contratação de um plano ou relatório.
      </p>
      <p>
        Você pode revogar o consentimento com este tratamento a qualquer tempo, bastando entrar em
        contato via e-mail suporte@phoenixos.com.br.
      </p>

      <h2>13. Contato</h2>
      <p>suporte@phoenixos.com.br</p>
    </LegalPageShell>
  );
}

export function PoliticaDePrivacidade() {
  return (
    <LegalPageShell titulo="Política de Privacidade — Phoenix OS" atualizadoEm="13/10/2026">
      <h2>1. Quem somos</h2>
      <p>
        O Phoenix OS é um produto operado por Vantum Tecnologia (Samuel Benevides do Nascimento
        LTDA), CNPJ 69.171.174/0001-13, com sede em R. Visconde do Rio Branco, 1488 - Centro,
        Curitiba - PR.
      </p>
      <p>Para qualquer dúvida sobre esta política ou sobre seus dados, entre em contato pelo e-mail: suporte@phoenixos.com.br.</p>

      <h2>2. Quais dados coletamos</h2>
      <ul>
        <li><strong>Dados de cadastro:</strong> nome, e-mail, CPF ou CNPJ, informados no momento da criação de conta ou da assinatura de um plano.</li>
        <li><strong>Dados financeiros da empresa analisada:</strong> faturamento, custos, dívidas, e demais informações inseridas por você para gerar diagnósticos, pareceres e relatórios. Esses dados podem ser sobre sua própria empresa (perfil Gestor) ou sobre empresas de terceiros que você atende (perfil Consultor) — nesse segundo caso, é sua responsabilidade garantir que tem autorização para inserir esses dados no Phoenix OS.</li>
        <li><strong>Dados de pagamento:</strong> processados diretamente pelo Asaas (nosso parceiro de pagamentos). O Phoenix OS nunca tem acesso a número de cartão de crédito ou senha bancária.</li>
        <li><strong>Dados de uso:</strong> como você usa o app (telas acessadas, ações realizadas), coletados automaticamente, para melhorar o produto e para suporte técnico.</li>
      </ul>

      <h2>3. Para que usamos esses dados</h2>
      <ul>
        <li>Gerar os diagnósticos, pareceres e relatórios que são a função principal do produto (processados por inteligência artificial da Anthropic, ver seção 4)</li>
        <li>Processar pagamentos e gerenciar sua assinatura</li>
        <li>Comunicar sobre sua conta (confirmações, avisos de cobrança, suporte)</li>
        <li>Melhorar o produto e corrigir problemas técnicos</li>
        <li>Cumprir obrigações legais e fiscais</li>
      </ul>

      <h2>4. Com quem compartilhamos dados</h2>
      <p>A Phoenix OS não comercializa dados pessoais. O compartilhamento ocorre apenas quando necessário à prestação dos serviços, com fornecedores como:</p>
      <table>
        <thead>
          <tr><th>Empresa</th><th>Função</th><th>Dados envolvidos</th></tr>
        </thead>
        <tbody>
          <tr><td>Google Firebase (Google Cloud)</td><td>Hospedagem, banco de dados, autenticação</td><td>Todos os dados da conta</td></tr>
          <tr><td>Anthropic (Claude API)</td><td>Processamento de IA para gerar os pareceres</td><td>Dados financeiros inseridos, de forma anonimizada quando possível</td></tr>
          <tr><td>Asaas</td><td>Processamento de pagamentos</td><td>Dados de cobrança (não temos acesso ao cartão)</td></tr>
          <tr><td>Brevo</td><td>Envio de e-mails</td><td>Nome e e-mail</td></tr>
          <tr><td>Cloudflare</td><td>Rede, DNS e segurança do domínio</td><td>Dados técnicos de acesso</td></tr>
        </tbody>
      </table>
      <p>
        Os prestadores tratam os dados conforme suas respectivas funções e políticas de privacidade,
        observadas as exigências aplicáveis da LGPD. Recomendamos que o usuário consulte esses
        documentos para obter informações adicionais sobre as práticas adotadas por cada prestador.
      </p>
      <p>
        Alguns desses prestadores (como Google e Anthropic) podem tratar dados em servidores fora do
        Brasil. Essa transferência internacional ocorre com base no art. 33 da LGPD, com a adoção de
        garantias contratuais e de segurança adequadas.
      </p>

      <h2>5. Onde seus dados ficam armazenados</h2>
      <p>
        Os dados pessoais tratados pela Phoenix OS são armazenados e processados por meio de
        infraestrutura tecnológica própria ou fornecida por prestadores de serviços de computação em
        nuvem, incluindo serviços disponibilizados pelo Google Cloud e pelo Firebase.
      </p>

      <h2>6. Por quanto tempo guardamos seus dados</h2>
      <p>
        Mantemos os seus dados enquanto forem necessários para as finalidades desta Política, para o
        cumprimento de obrigações legais, regulatórias e fiscais e para o exercício regular de
        direitos. Você pode pedir a exclusão da sua conta e dos seus dados a qualquer momento (veja{' '}
        <a href="/excluir-conta" className="underline text-white">como excluir sua conta</a>);
        a exclusão apaga o seu perfil, o seu login e os seus relatórios, ressalvadas as hipóteses de
        conservação autorizadas ou exigidas por lei. Contas de teste sem cadastro (anônimas) abandonadas
        são removidas automaticamente.
      </p>

      <h2>7. Seus direitos (LGPD, Art. 18)</h2>
      <p>Você pode, a qualquer momento, solicitar pelo e-mail de suporte:</p>
      <ul>
        <li>Confirmação de que tratamos seus dados</li>
        <li>Acesso aos dados que temos sobre você</li>
        <li>Correção de dados incompletos ou desatualizados</li>
        <li>Exclusão dos seus dados (respeitadas obrigações legais)</li>
        <li>Portabilidade dos dados para outro fornecedor</li>
        <li>Revogação do consentimento, quando aplicável</li>
        <li>Informação sobre com quem compartilhamos seus dados</li>
      </ul>

      <h2>8. Como seus dados são tratados</h2>
      <p>
        A Phoenix OS poderá tratar dados pessoais por meios eletrônicos, automatizados ou, quando
        necessário, manuais, exclusivamente para as finalidades informadas nesta Política. São
        adotadas medidas técnicas e administrativas destinadas a proteger os dados contra acesso não
        autorizado, perda, alteração, divulgação ou tratamento inadequado ou ilícito.
      </p>

      <h2>9. Segurança</h2>
      <p>
        Usamos criptografia (AES-256) e controles de acesso para proteger seus dados. Nenhum sistema
        é 100% invulnerável, mas trabalhamos continuamente para manter essas proteções atualizadas.
      </p>

      <h2>10. Menores de idade</h2>
      <p>O Phoenix OS é destinado a maiores de 18 anos e a empresas. Não coletamos intencionalmente dados de menores de idade.</p>

      <h2>11. Alterações desta política</h2>
      <p>Podemos atualizar esta política periodicamente. Mudanças relevantes serão comunicadas por e-mail ou dentro do app.</p>

      <h2>12. Controlador de dados</h2>
      <p>
        O papel de Controlador e de Operador é exercido por Vantum Tecnologia (Samuel Benevides do
        Nascimento LTDA). No perfil Consultor, a coleta dos dados dos clientes do Consultor é de
        responsabilidade, em parte, do próprio Consultor; já o tratamento desses dados dentro da
        plataforma é feito exclusivamente pela Phoenix OS.
      </p>

      <h2>13. Encarregado de Proteção de Dados Pessoais</h2>
      <p>O papel de Encarregado é exercido por Samuel Benevides do Nascimento, podendo este ser contatado no e-mail suporte@phoenixos.com.br.</p>
    </LegalPageShell>
  );
}
